/* ==========================================================================
   Wheel stage
   Only `transform` and `opacity` are ever animated here — filters and shadows
   stay static so a spin never leaves the compositor.
   ========================================================================== */

.stage {
  position: relative;
  /*
   * Sized from the room actually left, not a fixed cap or a fraction of the
   * viewport. On a wide window the height runs out first, and everything above
   * and below the wheel — top bar, page padding, the card's own header and call
   * to action — is a *constant* ~370px, not a percentage. Subtracting it is what
   * makes the wheel fill a 1440p screen and still fit a 720p one; a plain `vh`
   * term can only be right at one height.
   *
   * `clamp` floors it so a very short window shrinks the wheel instead of
   * resolving to a negative width and collapsing it to nothing.
   */
  width: clamp(240px, min(100%, calc(100dvh - 370px)), 820px);
  aspect-ratio: 1;
  margin-inline: auto;
  isolation: isolate;
  /* No `contain: paint` here — the pointer and the ambient glow deliberately
     overflow the stage box and would be clipped by it. */
}

/* --- the creator's scene -------------------------------------------------- */

/* Sits furthest back, behind the glow and the disc. Empty on the house theme
   (`--wheel-scene: none` with zero opacity), so nothing is paid for until a
   creator actually has a world. Static: no animation, no filter — this layer is
   under a spinning wheel and must never ask the compositor for anything. */
/* The artwork's archway, sized and nudged so it frames the disc.
   `--wheel-scene-size` blows the painting up until its opening matches the
   wheel, and `--wheel-scene-y` slides it so the two centres meet — every
   painting frames its opening differently, so both are per-theme numbers tuned
   by eye against the real image.

   The element clips: at this magnification the scene is nearly twice the stage,
   and without `overflow: hidden` it would paint straight over the card's header
   and the panels beside it. The wider garden is not lost — the page backdrop
   carries it, dimmed, behind everything. */
.scene {
  position: absolute;
  inset: -14%;
  z-index: 0;
  overflow: hidden;
  border-radius: 50%;
  background:
    var(--scene-image) 50% var(--wheel-scene-y) / var(--wheel-scene-size) no-repeat,
    var(--scene-wash);
  opacity: var(--wheel-scene-opacity);
  /* Fades out before the edge so the scene reads as depth around the wheel
     rather than a disc of its own sitting inside the card. */
  mask-image: radial-gradient(closest-side, #000 74%, transparent 100%);
  pointer-events: none;
}

/* --- ambient glow --------------------------------------------------------- */

/* The glow's bright core is unchanged; only its faint outer edge is pulled in,
   so a wheel sized to the column still fades out inside the card that clips it
   instead of ending on a hard line at the card's border. */
.ambient {
  position: absolute;
  inset: -11%;
  z-index: 0;
  border-radius: 50%;
  background:
    radial-gradient(circle at 50% 42%, var(--brand-glow) 0%, transparent 56%),
    radial-gradient(circle at 50% 58%, var(--wheel-ambient-2) 0%, transparent 52%);
  opacity: 0.55;
  filter: blur(28px);
  animation: breathe 6.5s ease-in-out infinite;
  pointer-events: none;
}

@keyframes breathe {
  0%,
  100% {
    opacity: 0.45;
    transform: scale(0.97);
  }
  50% {
    opacity: 0.68;
    transform: scale(1.03);
  }
}

/* --- rotating disc -------------------------------------------------------- */

.rotor {
  position: absolute;
  inset: 0;
  z-index: 1;
  will-change: transform;
  transform-origin: 50% 50%;
  /* Sits the disc in its housing: dark contact shadow, no coloured spill. */
  filter: drop-shadow(0 18px 34px rgba(0, 0, 0, 0.55));
}

.wheelSvg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

/* --- slice labels --------------------------------------------------------- */

/* Labels run along the radius, so every slice gets the full width of the band
   no matter how many there are. Font sizes come from `fitFontSize` in the
   geometry — the rules here only handle weight, colour and legibility. */
.labels {
  paint-order: stroke fill;
}

/* Full white with a dark halo: the icon has to hold up over a gold slice and a
   near-black one, and a translucent stroke disappears on the light ones. */
.labelIcon {
  color: #fff;
  overflow: visible;
  filter: drop-shadow(0 0 0.9px rgba(0, 0, 0, 0.85)) drop-shadow(0 0.5px 1px rgba(0, 0, 0, 0.5));
}

/* Text sits on saturated fills, so it carries its own dark outline rather than
   relying on the slice colour being dark enough. */
.labelPrimary,
.labelSecondary {
  fill: #fff;
  font-family: var(--font-sans);
  stroke: rgba(0, 0, 0, 0.5);
  paint-order: stroke fill;
}

.labelPrimary {
  font-weight: 800;
  letter-spacing: -0.01em;
  stroke-width: 1.15px;
}

.labelSecondary {
  fill: rgba(255, 255, 255, 0.86);
  font-weight: 700;
  letter-spacing: 0.1em;
  stroke-width: 0.8px;
  text-transform: uppercase;
}

/* --- housing: rim, pegs, needle ------------------------------------------- */

/* Gradient stops driven from CSS. SVG `stop-color` accepts a custom property,
   which is what lets one SVG wear every theme — the alternative was a copy of
   the whole wheel per skin. */
.rim1 {
  stop-color: var(--wheel-rim-1);
}
.rim2 {
  stop-color: var(--wheel-rim-2);
}
.rim3 {
  stop-color: var(--wheel-rim-3);
}
.rim4 {
  stop-color: var(--wheel-rim-4);
}
.rim5 {
  stop-color: var(--wheel-rim-5);
}

.rimHairline {
  stroke: var(--wheel-rim-hairline);
}

.pegTop {
  stop-color: var(--wheel-peg-top);
}
.pegBottom {
  stop-color: var(--wheel-peg-bottom);
}

.needle1 {
  stop-color: var(--wheel-needle-1);
}
.needle2 {
  stop-color: var(--wheel-needle-2);
}
.needle3 {
  stop-color: var(--wheel-needle-3);
}
.needle4 {
  stop-color: var(--wheel-needle-4);
}

.needleHousing {
  fill: var(--wheel-needle-housing);
}

.needleJewel {
  fill: var(--wheel-needle-jewel);
}

/* --- fixed lighting ------------------------------------------------------- */

.overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

/* --- pointer -------------------------------------------------------------- */

/* Sized so the needle's tip stops just inside the slice edge, right after it
   clears the peg ring — deep enough to look engaged, not skewered. */
.pointerMount {
  position: absolute;
  top: -5.8%;
  left: 50%;
  z-index: 4;
  width: 11.5%;
  transform: translateX(-50%);
  pointer-events: none;
}

.pointer {
  /* Pivots at the mounting screw, not the tip. */
  transform-origin: 50% 44%;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.6));
}

.pointerSvg {
  display: block;
  width: 100%;
  height: auto;
}

/* --- hub ------------------------------------------------------------------ */

.hub {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 5;
  display: grid;
  place-items: center;
  width: 26%;
  aspect-ratio: 1;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--border-strong);
  border-radius: 50%;
  background:
    radial-gradient(circle at 34% 28%, rgba(255, 255, 255, 0.16), transparent 58%),
    var(--wheel-hub-ring);
  box-shadow:
    0 10px 26px rgba(0, 0, 0, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.14),
    0 0 0 6px rgba(10, 10, 15, 0.75);
  color: var(--text-primary);
  cursor: pointer;
  transform: translate(-50%, -50%);
  transition:
    transform var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out);
}

.hub:hover:not(:disabled) {
  transform: translate(-50%, -50%) scale(1.05);
  box-shadow:
    0 12px 30px rgba(0, 0, 0, 0.65),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 0 0 6px rgba(10, 10, 15, 0.75),
    0 0 26px -2px var(--brand-glow);
}

.hub:active:not(:disabled) {
  transform: translate(-50%, -50%) scale(0.97);
}

.hub:focus-visible {
  outline: 2px solid var(--brand-400);
  outline-offset: 3px;
}

.hub:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

/* Slow conic sweep — the only thing that says "this is alive" at rest. */
.hubShimmer {
  position: absolute;
  inset: -50%;
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    var(--wheel-hub-shimmer) 40deg,
    transparent 90deg,
    transparent 360deg
  );
  opacity: 0.5;
  animation: sweep 4.5s linear infinite;
  pointer-events: none;
}

@keyframes sweep {
  to {
    transform: rotate(1turn);
  }
}

.hubFace {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 1px;
  place-items: center;
  width: calc(100% - 8px);
  aspect-ratio: 1;
  border-radius: 50%;
  background: var(--wheel-hub-face);
}

.hubLabel {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

/* --- responsive ----------------------------------------------------------- */

/* Matches the row layout in `PlayableWheel.module.css`: with the header and the
   call to action moved beside the wheel, the only things left above and below it
   are the top bar and two paddings — a much smaller constant, so the wheel keeps
   its size in a frame that would otherwise crush it. */
@media (min-width: 900px) and (max-height: 780px) and (min-aspect-ratio: 2 / 1) {
  .stage {
    /* Smaller constant than the stacked layout's: with the header and the call
       to action moved beside the wheel, only the top bar and two paddings sit
       above and below it. */
    width: clamp(200px, min(100%, calc(100dvh - 170px)), 820px);
  }
}

@media (max-width: 640px) {
  .stage {
    /* On a phone the wheel is the page — let it use the full width and only
       cap it against the viewport height. */
    width: min(100%, 66vh, 400px);
  }

  .hubLabel {
    font-size: 9px;
  }
}

/* --- reduced motion ------------------------------------------------------- */

@media (prefers-reduced-motion: reduce) {
  .ambient,
  .hubShimmer {
    animation: none;
  }

  .hub {
    transition: none;
  }
}
